import { FC, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Grid, GridSize, IconButton, Tooltip, Typography } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import LinkIcon from '@material-ui/icons/Link';
import RefreshIcon from '@material-ui/icons/Refresh';
import { StandardConfirmationDialog } from 'components/AppDialog';
import { useIsTabActive } from 'contexts/TabContext';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { getCurrentCustomerBooking } from 'selectors';
import { GET_SERVICE_BOOKING_ACCESS_URL, REGENERATE_SERVICE_BOOKING_ACCESS_URL } from 'constants/url';

interface Props {
  serviceId: number;
  handleSnackbar?: (variant: 'success' | 'error', message: string) => void;
  // Column widths (sm) of the label and the value, to fit the grid this is placed in
  labelWidth?: GridSize;
  valueWidth?: GridSize;
}

interface BookingAccess {
  bookingPin: string;
  bookingLink: string;
}

/**
 * Booking PIN for the customer booking page, with the booking link to copy and a way to replace both.
 * Rendered as one label/value pair inside the grey information box of the quotation and job detail pages.
 * Hidden (and nothing fetched) for tenants that do not use customer booking (Tenant.isBookingEnabled = false).
 */
const BookingAccessInfo: FC<Props> = ({ serviceId, handleSnackbar, labelWidth = 2, valueWidth = 4 }) => {
  const isTabActive = useIsTabActive();
  const { currentUser } = useContext(CurrentUserContext);
  const isBookingEnabled = getCurrentCustomerBooking(currentUser);
  const [access, setAccess] = useState<BookingAccess | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

  useEffect(() => {
    if (!serviceId || !isBookingEnabled) {
      return;
    }

    const cancelTokenSource = axios.CancelToken.source();

    const fetchAccess = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(GET_SERVICE_BOOKING_ACCESS_URL(serviceId), { cancelToken: cancelTokenSource.token });
        setAccess(response?.data || null);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Failed to load booking PIN', err);
          setAccess(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccess();

    return () => cancelTokenSource.cancel();
  }, [serviceId, isBookingEnabled]);

  const handleCopyLink = async () => {
    if (!access) {
      return;
    }

    try {
      await navigator.clipboard.writeText(access.bookingLink);
      handleSnackbar && handleSnackbar('success', 'Booking link copied');
    } catch (err) {
      handleSnackbar && handleSnackbar('error', 'Failed to copy booking link');
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const response = await axios.post(REGENERATE_SERVICE_BOOKING_ACCESS_URL(serviceId));
      if (!response?.data) {
        throw new Error('Network Error');
      }
      setAccess(response.data);
      handleSnackbar && handleSnackbar('success', 'New booking link and PIN generated');
    } catch (err) {
      console.error('Failed to regenerate booking PIN', err);
      handleSnackbar && handleSnackbar('error', 'Failed to generate a new booking link and PIN');
    } finally {
      setIsRegenerating(false);
      setOpenConfirm(false);
    }
  };

  if (!isBookingEnabled) {
    return null;
  }

  return (
    <>
      <Grid item xs={12} sm={labelWidth}>
        <Typography variant='body2' color='textSecondary'>
          Booking PIN
        </Typography>
      </Grid>
      <Grid item xs={12} sm={valueWidth}>
        {isLoading ? (
          <Skeleton width={'40%'} />
        ) : access ? (
          <Grid container alignItems='center'>
            <Typography variant='subtitle2' style={{ letterSpacing: 2, marginRight: 8 }}>
              {access.bookingPin}
            </Typography>
            <Tooltip title='Copy booking link'>
              <IconButton size='small' color='primary' aria-label='Copy booking link' onClick={handleCopyLink}>
                <LinkIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Generate new booking link and PIN'>
              <IconButton size='small' color='primary' aria-label='Generate new booking link and PIN' onClick={() => setOpenConfirm(true)}>
                <RefreshIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Grid>
        ) : (
          '-'
        )}
      </Grid>
      {isTabActive && openConfirm && (
        <StandardConfirmationDialog
          variant={'warning'}
          title='Generate New Booking Link & PIN'
          message={`The booking link and PIN the customer has now will stop working immediately.\nAre you sure you want to proceed?`}
          okLabel='OK'
          cancelLabel='cancel'
          open={openConfirm}
          isLoading={isRegenerating}
          handleClose={() => setOpenConfirm(false)}
          onConfirm={handleRegenerate}
        />
      )}
    </>
  );
};

export default BookingAccessInfo;
